import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { SectionHeader, Loader, Lu, CHART_COLORS, fmtSAR } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FILES = [
  { name: "deck.pdf", icon: "file-text", ar: "العرض التقديمي (PDF)", en: "Pitch Deck (PDF)" },
  { name: "deck.pptx", icon: "presentation", ar: "العرض التقديمي (PowerPoint)", en: "Pitch Deck (PowerPoint)" },
  { name: "study.pdf", icon: "book-open", ar: "دراسة المستثمر (PDF)", en: "Investor Study (PDF)" },
];

async function downloadFile(name, lang) {
  const url_ = name === "bundle" ? `/investor/bundle?lang=${lang}` : `/investor/files/${name}?lang=${lang}`;
  const fn = name === "bundle" ? `Madarek-Investor-Pack-${lang}.zip` : `Madarek-${name.split(".")[0]}-${lang}.${name.split(".")[1]}`;
  const r = await client.get(url_, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = Object.assign(document.createElement("a"), { href: url, download: fn });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

const HERO = "https://images.unsplash.com/photo-1605237165959-dcc784975d51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBzYXVkaSUyMGFyYWJpYSUyMHJpeWFkaCUyMGludGVyaW9yJTIwZHVza3xlbnwwfHx8fDE3ODg5MzkwNzF8MA&ixlib=rb-4.1.0&q=85";
const FUND_COLORS = ["#1E5EFF", "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#F43F5E"];

export default function InvestorPack() {
  const { t, lang } = useLang();
  const [fin, setFin] = useState(null);
  const [slides, setSlides] = useState([]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    Promise.all([client.get("/investor/financials"), client.get("/investor/deck")])
      .then(([f, d]) => { setFin(f.data); setSlides(d.data.slides); });
  }, []);
  if (!fin) return <Loader />;
  const L = lang === "ar";

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(fin.years.map((y) => ({
      Year: y.year, Properties: y.properties, Rooms: y.rooms, "ARR SaaS": y.arr_saas,
      "Hardware Rev": y.hw_revenue, "Total Revenue": y.total_revenue, "Gross Profit": y.gross_profit, "Net": y.net,
    }))), "P&L 3-Year");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(fin.funding_use.map((f) => ({
      Item: f.item_en, Amount_SAR: f.amount, Percent: f.pct,
    }))), "Use of Funds");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(fin.pricing.map((p) => ({
      Plan: p.plan, "Price SAR/room/mo": p.price, Segment: p.segment_en,
    }))), "Pricing");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(
      Object.entries(fin.unit_economics).map(([k, v]) => [k, v])), "Unit Economics");
    XLSX.writeFile(wb, "Madarek_Financial_Model.xlsx");
    toast.success(t("exportFinancials"));
  };

  const revChart = fin.years.map((y) => ({ name: L ? y.year_ar : y.year, ARR: y.arr_saas, Hardware: y.hw_revenue, Net: y.net }));

  return (
    <div>
      <SectionHeader icon="trending-up" title={t("investor")} subtitle={`${t("preSeed")} · 1,500,000 ${t("sar")}`}>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="download-all" className="md-gold-gradient text-primary-foreground font-bold border-0"
            onClick={() => toast.promise(downloadFile("bundle", lang), { loading: L ? "جارٍ تجهيز الحزمة (PDF + PPTX + Excel)..." : "Preparing bundle (PDF + PPTX + Excel)...", success: L ? "تم تحميل الحزمة الكاملة" : "Full pack downloaded", error: L ? "فشل التحميل" : "Download failed" })}>
            <Lu name="package" className="w-4 h-4 ltr:mr-2 rtl:ml-2" />{L ? "تحميل الكل (ZIP)" : "Download All (ZIP)"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="download-deck" className="bg-foreground/5 hover:bg-foreground/10 border border-border text-foreground/80">
                <Lu name="file-down" className="w-4 h-4 ltr:mr-2 rtl:ml-2" />{t("downloadDeck")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover border-border text-foreground">
              {FILES.map((f) => (
                <DropdownMenuItem key={f.name} data-testid={`download-${f.name.replace(".", "-")}`} className="cursor-pointer focus:bg-foreground/10"
                  onClick={() => { toast.promise(downloadFile(f.name, lang), { loading: L ? "جارٍ التجهيز..." : "Preparing...", success: L ? "تم التحميل" : "Downloaded", error: L ? "فشل التحميل" : "Download failed" }); }}>
                  <Lu name={f.icon} className="w-4 h-4 ltr:mr-2 rtl:ml-2 text-primary" />{L ? f.ar : f.en}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="export-financials" onClick={exportExcel} className="md-gold-gradient text-primary-foreground font-bold border-0">
            <Lu name="sheet" className="w-4 h-4 ltr:mr-2 rtl:ml-2" />{t("exportFinancials")}
          </Button>
        </div>
      </SectionHeader>

      <Tabs defaultValue="deck">
        <TabsList className="bg-foreground/5 border border-border mb-5 flex-wrap h-auto">
          {[["deck", "pitchDeck"], ["financials", "financialModel"], ["unit", "unitEconomics"], ["pricing", "pricing"], ["funding", "funding"]].map(([v, k]) => (
            <TabsTrigger key={v} value={v} data-testid={`inv-tab-${v}`}
              className="data-[state=active]:bg-primary data-[state=active]:shadow-md data-[state=active]:text-primary-foreground text-foreground/80">{t(k)}</TabsTrigger>
          ))}
        </TabsList>

        {/* Pitch Deck */}
        <TabsContent value="deck">
          <div className="md-card overflow-hidden relative" data-testid="deck-viewer">
            <div className="aspect-[16/9] relative flex flex-col">
              <AnimatePresence mode="wait">
                <motion.div key={slide} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.3 }} className="absolute inset-0">
                  {slide === 0 ? (
                    <div className="relative w-full h-full">
                      <img src={HERO} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(6,10,18,0.9), rgba(6,10,18,0.7))" }} />
                      <div className="relative z-10 h-full flex flex-col justify-center p-8 md:p-14">
                        <span className="text-primary text-sm font-semibold tracking-widest uppercase">{L ? slides[0].tag_ar : slides[0].tag_en}</span>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-white mt-3">{L ? slides[0].title_ar : slides[0].title_en}</h2>
                        <p className="text-white/85 mt-4 max-w-2xl text-sm md:text-base leading-relaxed">{L ? slides[0].body_ar : slides[0].body_en}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col justify-center p-8 md:p-14 bg-card">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="w-10 h-10 rounded-lg md-gold-gradient flex items-center justify-center text-primary-foreground font-extrabold">{slides[slide].n}</span>
                        <span className="text-primary text-xs font-semibold tracking-widest uppercase">{L ? slides[slide].tag_ar : slides[slide].tag_en}</span>
                      </div>
                      <h2 className="text-2xl md:text-4xl font-extrabold text-foreground">{L ? slides[slide].title_ar : slides[slide].title_en}</h2>
                      <p className="text-foreground/80 mt-5 max-w-3xl text-sm md:text-lg leading-relaxed">{L ? slides[slide].body_ar : slides[slide].body_en}</p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex items-center justify-between p-4 border-t border-border bg-card">
              <Button data-testid="deck-prev" onClick={() => setSlide((s) => Math.max(0, s - 1))} disabled={slide === 0}
                className="bg-foreground/5 border border-border text-foreground/80 h-9"><Lu name="chevron-right" className="w-4 h-4 rtl:block ltr:hidden" /><Lu name="chevron-left" className="w-4 h-4 ltr:block rtl:hidden" /></Button>
              <div className="flex gap-1.5 flex-wrap justify-center">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setSlide(i)} data-testid={`deck-dot-${i}`}
                    className="h-2 rounded-full transition-all" style={{ width: i === slide ? 24 : 8, background: i === slide ? "#1E5EFF" : "#334155" }} />
                ))}
              </div>
              <Button data-testid="deck-next" onClick={() => setSlide((s) => Math.min(slides.length - 1, s + 1))} disabled={slide === slides.length - 1}
                className="bg-foreground/5 border border-border text-foreground/80 h-9"><Lu name="chevron-left" className="w-4 h-4 rtl:block ltr:hidden" /><Lu name="chevron-right" className="w-4 h-4 ltr:block rtl:hidden" /></Button>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground/70 mt-3">{slide + 1} / {slides.length}</p>
        </TabsContent>

        {/* Financials */}
        <TabsContent value="financials">
          <div className="md-card p-5 mb-5">
            <h3 className="font-semibold text-foreground mb-4">{t("financialModel")} — 3 {L ? "سنوات" : "Years"} ({t("sar")})</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--md-soft))" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#94A3B8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => `${v / 1000}K`} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} formatter={(v) => `${fmtSAR(v)} SAR`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ARR" stackId="a" fill="#1E5EFF" name="ARR (SaaS)" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Hardware" stackId="a" fill="#3B82F6" name="Hardware" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="md-card p-1 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">{L ? "المؤشر" : "Metric"}</TableHead>
                {fin.years.map((y) => <TableHead key={y.year} className="text-primary text-center">{L ? y.year_ar : y.year}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {[
                  ["properties", L ? "عدد العقارات" : "Properties", (v) => v],
                  ["rooms", L ? "الغرف النشطة" : "Active Rooms", (v) => v],
                  ["arr_saas", "ARR (SaaS)", (v) => fmtSAR(v)],
                  ["hw_revenue", L ? "إيراد الأجهزة" : "Hardware Rev", (v) => fmtSAR(v)],
                  ["total_revenue", L ? "إجمالي الإيراد" : "Total Revenue", (v) => fmtSAR(v)],
                  ["gross_profit", L ? "الربح الإجمالي" : "Gross Profit", (v) => fmtSAR(v)],
                  ["net", L ? "الصافي" : "Net", (v) => fmtSAR(v)],
                ].map(([key, label, fmt]) => (
                  <TableRow key={key} className="border-border" data-testid={`fin-${key}`}>
                    <TableCell className="text-foreground/80 font-medium">{label}</TableCell>
                    {fin.years.map((y) => (
                      <TableCell key={y.year} className={`text-center font-mono ${key === "net" ? (y[key] >= 0 ? "text-emerald-400" : "text-rose-400") : "text-foreground/80"}`}>
                        {fmt(y[key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Unit economics */}
        <TabsContent value="unit">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ["cac_hotel", L ? "CAC لكل فندق" : "CAC / hotel", `${fmtSAR(fin.unit_economics.cac_hotel)}`, CHART_COLORS.amber],
              ["arpu", "ARPU/room/mo", `${fin.unit_economics.arpu_room_month}`, CHART_COLORS.gold],
              ["ltv", "LTV / room", `${fmtSAR(fin.unit_economics.ltv_room_36mo)}`, CHART_COLORS.emerald],
              ["ratio", "LTV : CAC", `${fin.unit_economics.ltv_cac_ratio}x`, CHART_COLORS.blue],
              ["gm_saas", L ? "هامش SaaS" : "SaaS Margin", `${fin.unit_economics.gross_margin_saas}%`, CHART_COLORS.emerald],
              ["gm_hw", L ? "هامش الأجهزة" : "HW Margin", `${fin.unit_economics.gross_margin_hw}%`, CHART_COLORS.blue],
              ["payback", L ? "فترة الاسترداد" : "Payback", `${fin.unit_economics.payback_months} ${L ? "شهر" : "mo"}`, CHART_COLORS.amber],
              ["churn", "Churn", `${fin.unit_economics.churn_annual}%`, CHART_COLORS.rose],
            ].map(([k, label, v, c]) => (
              <div key={k} className="md-card p-5" data-testid={`ue-${k}`}>
                <p className="text-xs text-muted-foreground/70">{label}</p>
                <p className="text-2xl font-extrabold font-mono mt-2" style={{ color: c }}>{v}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Pricing */}
        <TabsContent value="pricing">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {fin.pricing.map((p) => (
              <div key={p.plan} className="md-card p-5 flex flex-col" data-testid={`plan-${p.plan}`}>
                <h3 className="text-lg font-bold text-foreground">{p.plan}</h3>
                <p className="text-xs text-muted-foreground/70 mt-0.5">{L ? p.segment_ar : p.segment_en}</p>
                <div className="my-4">
                  {p.price ? <p className="text-3xl font-extrabold text-primary font-mono">{p.price} <span className="text-sm text-muted-foreground/70">{t("sar")}{t("perMonth")}</span></p>
                    : <p className="text-2xl font-extrabold text-primary">{t("custom")}</p>}
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed flex-1">{L ? p.features_ar : p.features_en}</p>
              </div>
            ))}
          </div>
          <div className="md-card p-1 overflow-x-auto mt-5">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">{t("hardware")}</TableHead><TableHead className="text-primary text-end">{t("sar")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {fin.hardware_pricing.map((h, i) => (
                  <TableRow key={i} className="border-border">
                    <TableCell className="text-foreground/80">{L ? h.item_ar : h.item_en}</TableCell>
                    <TableCell className="text-end font-mono text-primary">{fmtSAR(h.price)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Funding */}
        <TabsContent value="funding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="md-card p-5">
              <h3 className="font-semibold text-foreground mb-4">{t("funding")} — 1.5M {t("sar")}</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={fin.funding_use} dataKey="pct" nameKey={L ? "item_ar" : "item_en"} innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {fin.funding_use.map((_, i) => <Cell key={i} fill={FUND_COLORS[i]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} formatter={(v, n) => [`${v}%`, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {fin.funding_use.map((f, i) => (
                <div key={i} className="md-card p-4 flex items-center gap-3" data-testid={`fund-${i}`}>
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: FUND_COLORS[i] }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">{L ? f.item_ar : f.item_en}</p>
                    <div className="w-full bg-foreground/5 rounded-full h-1.5 mt-2">
                      <div className="h-1.5 rounded-full" style={{ width: `${f.pct}%`, background: FUND_COLORS[i] }} />
                    </div>
                  </div>
                  <div className="text-end shrink-0">
                    <p className="text-sm font-mono text-primary">{fmtSAR(f.amount)}</p>
                    <p className="text-xs text-muted-foreground/70">{f.pct}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
